// ==============================
// Imports
// ==============================

// MUI Components
import {
  Done,
  FileDownload,
  Visibility,
  Search,
  ManageSearch,
} from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Stack,
  Chip,
  IconButton,
  Typography,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
} from '@mui/material';
import { TableNoData, TableSpacerRow } from './common/TableEmptyState';
import TableLoader from './common/TableLoader';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

// Libraries
import { QRCodeCanvas } from 'qrcode.react';

// Local Components
import {
  levelAlternativeColors,
  levelColors,
  // levelAlternativeColors,
  // levelColors,
  serviceColors,
  serviceTypeColors,
} from '../utils/colorHandler';
import ColorChip from './common/ColorChip';
import {
  rowHoverBg,
  selectedRowBg,
  selectedRowBgOpaque,
  walkInRowBg,
  walkInRowBgOpaque,
} from '../utils/rowHighlight';
// import { notifyActionSuccess } from '../utils/toast';

// Hook
import { useAssessmentController } from '../hooks/useAssessmentTable';
// import ReuseableModal from './ReuseableModal';
// import CourseStageAccordion from './CourseStageAccordion';
import {
  ServiceType,
  Subsubtype,
  Subtype,
} from '../types/ServiceAccordion.type';
import AttendanceDialog from './FormDialog/AttendanceDialog';
import { CourseLevel } from '../types/CourseStageAccordion.type';
import CourseLevelLogDialog from './FormDialog/CourseLeveLogDialog';
import { useTranslation } from '../i18n';

const AssessmentTable = () => {
  const { t } = useTranslation();
  const {
    registrations,
    page,
    limit,
    totalItems,
    searchTerm,
    openDialog,
    emptyRows,
    serviceTypeFilter,
    subTypeFilter,
    subSubTypeFilter,
    statusFilter,
    setStatusFilter,
    anonymousFilter,
    setAnonymousFilter,
    joinableFilter,
    setJoinableFilter,
    setPage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterChange,
    handleFilterSubtypeChange,
    handleFilterSubSubtypeChange,
    handleDialogOpen,
    handleDialogClose,
    handleDialogConfirm,
    fetchRegistrations,
    handleDownloadAssessments,
    downloadLoading,
    serviceOptions,
    clearFilters,

    // selectedRegistration,
    // isOpenLevelModal,
    // handleOpenLevelModal,
    // handleCloseLevelModal,
    // courseStages,
    // fetchUserStageProgress,

    selectedId,
    openAttendanceDialog,
    handleDialogOpenAttendance,
    handleDialogCloseAttendance,
    handleUndoAttendance,

    handleDialogOpenJoinAssessment,
    handleDialogCloseJoinAssessment,
    openJoinAssessmentDialog,
    handleDialogConfirmJoinAssessment,

    openPromotionHistoryDialog,
    handleDialogOpenPromotionHistoryDialog,
    handleDialogClosePromotionHistoryDialog,

    handleFilterLevelChange,
    levelOptions,
    levelFilter,

    lastSyncTime,
    highlightedRow,
    setHighlightedRow,

    loading,
  } = useAssessmentController();

  // ==============================
  // Render
  // ==============================
  return (
    <Paper>
      <Box
        p={1.5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <TextField
          size="medium"
          placeholder={t('assessment.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              fetchRegistrations();
              setPage(1);
              // notifyActionSuccess('Search');
            }
          }}
          sx={{ width: 270 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              size: 'small',
            },
          }}
        />
        <Box display={'flex'} sx={{ gap: 0.5 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={serviceTypeFilter?.id ?? ''}
              onChange={handleFilterChange}
            >
              <MenuItem value="-1">{t('assessment.allProducts')}</MenuItem>
              {serviceOptions.map((option: ServiceType) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={subTypeFilter?.id ?? ''}
              onChange={handleFilterSubtypeChange}
            >
              <MenuItem value="-1">{t('assessment.allServiceTypes')}</MenuItem>
              {serviceTypeFilter?.subtypes.map((option: Subtype) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={subSubTypeFilter?.id ?? ''}
              onChange={handleFilterSubSubtypeChange}
            >
              <MenuItem value="-1">{t('assessment.allPackages')}</MenuItem>
              {subTypeFilter?.subsubtypes.map((option: Subsubtype) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <MenuItem value="all">{t('assessment.allStatus')}</MenuItem>
              <MenuItem value="active">{t('assessment.statusActive')}</MenuItem>
              <MenuItem value="inactive">
                {t('assessment.statusInactive')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={anonymousFilter}
              onChange={(event) => setAnonymousFilter(event.target.value)}
            >
              <MenuItem value="all">{t('assessment.allCustomers')}</MenuItem>
              <MenuItem value="anonymous">
                {t('assessment.anonymousOnly')}
              </MenuItem>
              <MenuItem value="non_anonymous">
                {t('assessment.nonAnonymousOnly')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <Select
              displayEmpty
              value={joinableFilter}
              onChange={(event) => setJoinableFilter(event.target.value)}
            >
              <MenuItem value="all">{t('assessment.allStudents')}</MenuItem>
              <MenuItem value="available">
                {t('assessment.availableToJoin')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={levelFilter?.levelName ?? ''}
              onChange={handleFilterLevelChange}
            >
              <MenuItem value="All">{t('assessment.allLevels')}</MenuItem>
              {levelOptions?.map((option: CourseLevel, index: number) => (
                <MenuItem key={index} value={option.levelName}>
                  {option.levelName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Clear filters button */}
          {searchTerm ||
          serviceTypeFilter?.id !== '-1' ||
          statusFilter !== 'all' ||
          anonymousFilter !== 'all' ||
          joinableFilter !== 'all' ||
          levelFilter?.levelName !== 'All' ? (
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={clearFilters}
              startIcon={<FilterAltOffIcon />}
            >
              {t('assessment.clear')}
            </Button>
          ) : null}

          <Tooltip title={t('assessment.downloadTooltip')}>
            <IconButton
              onClick={handleDownloadAssessments}
              loading={downloadLoading}
              disabled={totalItems === 0 || downloadLoading}
            >
              <FileDownload />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <TableContainer>
        <Table size="small" padding="checkbox">
          {/* ********** */}
          {/* HEADER */}
          {/* ********** */}
          <TableHead>
            <TableRow sx={{ backgroundColor: '#4da7db' }}>
              {/* Service Sub-Headers */}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colUserId')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colTicket')}
                </Typography>
              </TableCell>
              {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  Full Name (Preferred Name)
                </Typography>
              </TableCell> */}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colFirstName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colLastName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colPreferredName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colLevel')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colProduct')}
                </Typography>
              </TableCell>
              {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  Service
                </Typography>
              </TableCell> */}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colType')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colDetails')}
                </Typography>
              </TableCell>

              {/* Attendance Sub-Headers */}
              {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  Start / End / Duration
                </Typography>
              </TableCell> */}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colStartDate')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colEndDate')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colTicketType')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colCheckinToday')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colCheckinBalance')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('assessment.colJoinAssessment')}
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  right: -16,
                  zIndex: '1 !important',
                }}
              >
                <Typography
                  fontWeight="bold"
                  color="#fff"
                  bgcolor={'#4da7db'}
                  borderLeft={1}
                  textAlign="center"
                  className="!px-2"
                >
                  {t('assessment.actions')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          {/* ********** */}
          {/* DATA MANIPULATION */}
          {/* ********** */}
          <TableBody>
            {loading ? (
              <TableLoader colSpan={16} />
            ) : (
              <>
                {registrations.map((registrations) => {
                  // Anonymous walk-in customer (no full info) — highlighted, same as
              // the Registration screen.
              const isAnonymous = registrations.userId === 999999;
              return (
                <TableRow
                  key={registrations.id}
                  sx={{
                    '&:hover': { backgroundColor: rowHoverBg },
                    backgroundColor:
                      highlightedRow === `data_${registrations.id}`
                        ? selectedRowBg
                        : isAnonymous
                          ? walkInRowBg
                          : 'inherit',
                    ...(isAnonymous && {
                      fontStyle: 'italic',
                      color: 'text.secondary',
                    }),
                  }}
                  onClick={() => {
                    if (highlightedRow === `data_${registrations.id}`) {
                      setHighlightedRow(null);
                    } else {
                      setHighlightedRow(`data_${registrations.id}`);
                    }
                  }}
                >
                  {/* Service information */}
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      ...(isAnonymous && { borderLeft: '3px solid #f0ad4e' }),
                    }}
                  >
                    {isAnonymous ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{registrations.userId}</span>
                        <Chip
                          label="Anonymous"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Stack>
                    ) : (
                      registrations.userId
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <QRCodeCanvas
                      value={registrations.ticket}
                      size={40}
                      marginSize={2}
                    />
                  </TableCell>
                  {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div>
                    {`${registrations.user.firstName} ${registrations.user.lastName} (${registrations.user.preferredName || 'None'})`}
                  </div>
                  <div
                    style={{
                      fontWeight: 'bold',
                      color: levelColors['Starfish'],
                    }}
                  >
                    Starfish
                  </div>
                  <div
                    style={{
                      fontWeight: 'bold',
                      color: levelAlternativeColors['Development'],
                    }}
                  >
                    Development
                  </div>
                </TableCell> */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>{`${registrations.user.firstName}`}</div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>{`${registrations.user.lastName}`}</div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const preferredName = registrations.user.preferredName;
                      // Treat empty AND the literal "None" string as no value.
                      const isEmpty =
                        !preferredName ||
                        preferredName.trim().toLowerCase() === 'none';
                      return isEmpty ? (
                        <span style={{ opacity: 0.4 }}>None</span>
                      ) : (
                        <div>{preferredName}</div>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const hasLevel =
                        registrations.serviceType === 'COURSE' &&
                        registrations.courseLevel;
                      // No level assigned -> faded "None".
                      if (!hasLevel)
                        return <span style={{ opacity: 0.4 }}>None</span>;
                      return (
                        <div
                          style={{
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            color: {
                              ...levelColors,
                              ...levelAlternativeColors,
                            }[registrations.courseLevel || ''],
                          }}
                        >
                          {registrations.courseLevel}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      color: serviceColors[registrations.serviceType],
                    }}
                  >
                    <ColorChip
                      label={registrations.serviceType}
                      palette={serviceColors}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip
                      label={registrations.subtype}
                      palette={serviceTypeColors}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip label={registrations.subsubtype.name} />
                  </TableCell>
                  {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div>{registrations.subtype || '---'}</div>
                  <div
                    style={{
                      fontSize: '1.2em',
                      color: serviceColors[registrations.serviceType],
                    }}
                  >
                    {registrations.subsubtype.name || '---'}
                  </div>
                  <div
                    style={{
                      fontSize: '0.8em',
                      color: 'text.primary',
                      fontWeight: 'bold',
                    }}
                  >
                    (Note: {registrations.note || '---'})
                  </div>
                </TableCell> */}
                  {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div>
                    {new Date(registrations.sos).toLocaleDateString()} -{' '}
                    {new Date(registrations.eos).toLocaleDateString()}
                  </div>
                  <div></div>
                  <div>{registrations.duration || '---'} day(s)</div>
                </TableCell> */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>
                      {new Date(registrations.sos).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>
                      {new Date(registrations.eos).toLocaleDateString()}
                    </div>
                  </TableCell>
                  {/* Service information */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>{registrations.visitType.toUpperCase() || '---'}</div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>{registrations.attendancesToday.length} time(s)</div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                      (Total checkin: {registrations.attendancesAll.length}{' '}
                      times)
                    </div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>
                      {registrations.visitType == 'count'
                        ? registrations.visitBalance
                        : `∞`}{' '}
                      time(s)
                    </div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {/* <div
                    style={{
                      fontWeight: 'bold',
                      backgroundColor:
                        registrations.serviceType === 'COURSE' &&
                        registrations.joined
                          ? '#67CB81'
                          : registrations.serviceType === 'COURSE' &&
                              !registrations.joined
                            ? '#FF9C9C'
                            : '',
                      color:
                        registrations.serviceType === 'COURSE' &&
                        registrations.joined
                          ? '#015C03'
                          : registrations.serviceType === 'COURSE' &&
                              !registrations.joined
                            ? '#922802'
                            : '',
                    }}
                    className="px-3 py-1 rounded-full text-center"
                  >{`${registrations.serviceType === 'COURSE' && registrations.joined ? 'YES' : registrations.serviceType === 'COURSE' && !registrations.joined ? 'NO' : 'N/A'}`}</div>
                   */}

                    {registrations.serviceType === 'COURSE' ? (
                      <div className="flex items-center justify-center">
                        <Switch
                          checked={registrations.joined}
                          size="small"
                          onChange={() =>
                            handleDialogOpenJoinAssessment(registrations.id)
                          }
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                        {'N/A'}
                      </div>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      right: -16,
                      zIndex: '1 !important',
                      // Opaque so scrolled columns don't bleed through the pinned
                      // Actions cell on tinted rows.
                      backgroundColor:
                        highlightedRow === `data_${registrations.id}`
                          ? selectedRowBgOpaque
                          : isAnonymous
                            ? walkInRowBgOpaque
                            : 'background.paper',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        backgroundColor:
                          highlightedRow === `data_${registrations.id}`
                            ? selectedRowBgOpaque
                            : isAnonymous
                              ? walkInRowBgOpaque
                              : 'background.paper',
                        borderLeft: 1,
                        borderColor: 'divider',
                        px: 2,
                      }}
                    >
                      <Tooltip title="Check in history">
                        <IconButton
                          color="primary"
                          aria-label="Check in history"
                          onClick={() =>
                            handleDialogOpenAttendance(registrations.id)
                          }
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Promotion history">
                        <IconButton
                          color="primary"
                          aria-label="Promotion history"
                          onClick={() =>
                            handleDialogOpenPromotionHistoryDialog(
                              registrations.id,
                            )
                          }
                          disabled={registrations.serviceType !== 'COURSE'}
                        >
                          <ManageSearch />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Check in">
                        <IconButton
                          color="primary"
                          aria-label="checkin assessment"
                          onClick={() => handleDialogOpen(registrations.id)}
                        >
                          <Done />
                        </IconButton>
                      </Tooltip>
                      {/* {registrations.serviceType === 'COURSE' && (
                      <Tooltip title="Join Assessment / Cancel">
                        <IconButton
                          color="primary"
                          aria-label="join assessment"
                          onClick={() =>
                            // handleOpenLevelModal(
                            //   registrations.subsubtypeId,
                            //   registrations,
                            // )
                            handleDialogOpenJoinAssessment(registrations.id)
                          }
                        >
                          <Verified />
                        </IconButton>
                      </Tooltip>
                    )} */}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
                {registrations.length === 0 ? (
                  <TableNoData />
                ) : (
                  <TableSpacerRow rows={emptyRows} />
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Other data information */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pl={2}
      >
        <Typography variant="body2" color="textSecondary">
          Last sync: {lastSyncTime?.format('DD/MM/YYYY - HH:mm:ss') || 'Never'}
        </Typography>
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Box>

      {/* Dialog 1*/}
      {/* Yes/ No dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Confirm Checkin</DialogTitle>
        <DialogContent>Are you sure to checkin for this user?</DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="contained" color="error">
            Close
          </Button>
          <Button
            onClick={handleDialogConfirm}
            variant="contained"
            color="primary"
            className="!text-white"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog 2 - Attendances List */}
      <AttendanceDialog
        open={openAttendanceDialog}
        handleClose={handleDialogCloseAttendance}
        registrationId={selectedId}
        handleUndoAttendance={handleUndoAttendance}
      />

      {/* Dialog 3 - Join Assessment */}
      <Dialog
        open={openJoinAssessmentDialog}
        onClose={handleDialogCloseJoinAssessment}
      >
        <DialogTitle>Update join status</DialogTitle>
        <DialogContent>
          Are you sure to update join status for this user?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogCloseJoinAssessment}
            variant="contained"
            color="error"
          >
            Close
          </Button>
          <Button
            onClick={handleDialogConfirmJoinAssessment}
            variant="contained"
            color="primary"
            className="!text-white"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog 4 - Join Assessment */}
      <CourseLevelLogDialog
        open={openPromotionHistoryDialog}
        handleClose={handleDialogClosePromotionHistoryDialog}
        registrationId={selectedId}
      />

      {/* Level Modal */}
      {/* <ReuseableModal
        isOpen={isOpenLevelModal}
        toggle={handleCloseLevelModal}
        width="w-4/6"
        height="h-4/5"
      >
        <div className="p-4 relative">
          <Typography variant="h6" color="secondary" fontWeight="bold">
            Manage User Level
          </Typography>
          <Typography variant="body1" color="text.accent" sx={{ mt: 2, mb: 2 }}>
            Promote or demote the user to a different level.
          </Typography>
          <div className="flex flex-col gap-2">
            <CourseStageAccordion
              data={courseStages}
              registration={selectedRegistration}
              fetchUserStageProgress={fetchUserStageProgress}
            />

            <div className="absolute top-3 right-3 flex justify-end gap-4 z-10">
              <IconButton onClick={handleCloseLevelModal}>
                <Close />
              </IconButton>
            </div>
          </div>
        </div>
      </ReuseableModal> */}
    </Paper>
  );
};

export default AssessmentTable;
